import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, tap } from 'rxjs';

import {
  TellentAssistantMastraStreamClient,
  type CurrentThreadMessages,
} from '@core/tellent-assistant-data';
import type { ChatMessage, ConversationStarterMessage } from '@core/tellent-assistant-types';
import { toMastraSystemMessage, type MastraInputMessage } from '@core/tellent-assistant-util';
import { generateUuid } from '@core/utils-legacy';

import { AssistantRunStore } from './assistant-run.store';
import { TellentAssistantAnalyticsService } from '../analytics/tellent-assistant-analytics.service';
import { TellentAssistantThreadsService } from '../threads/tellent-assistant-threads.service';

function skillNameFromArgs(args: unknown): string | null {
  return typeof args === 'object' &&
    args !== null &&
    'name' in args &&
    typeof args.name === 'string'
    ? args.name
    : null;
}

/**
 * A starter/typed message, optionally carrying metadata (persisted by Mastra
 * as `content.metadata`). Messages with `metadata.uiEvent` are hidden from
 * the transcript — the UI talking to the agent.
 */
export type OutgoingMessage = ConversationStarterMessage & {
  metadata?: Record<string, unknown>;
};

@Injectable({ providedIn: 'root' })
export class AssistantRunService {
  private readonly store = inject(AssistantRunStore);
  private readonly client = inject(TellentAssistantMastraStreamClient);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly analytics = inject(TellentAssistantAnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seenToolCallIds = new Set<string>();
  private readonly pendingInput = signal<readonly MastraInputMessage[] | null>(null);

  public readonly messages = this.store.messages;
  public readonly runStatus = this.store.runStatus;
  public readonly currentThreadId = this.store.currentThreadId;
  public readonly lastError = this.store.lastError;
  public readonly isRunning = this.store.isRunning;
  public readonly canRetry = computed(() => this.pendingInput() !== null);

  constructor() {
    this.client.messages$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(msgs => {
      this.trackSkillUsage(msgs);
      this.store.replaceMessages([...msgs]);
    });
    this.client.runError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(err => this.store.markRunError(err.message));
    this.client.runFinalized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.store.runStatus() === 'running') this.store.markRunEnd();
      this.threadsService.loadThreads();
    });

    const syncFromCurrentThread = rxMethod<CurrentThreadMessages | null>(
      (source$: Observable<CurrentThreadMessages | null>) =>
        source$.pipe(
          tap((t: CurrentThreadMessages | null) => {
            if (t === null || t.threadId !== this.store.currentThreadId()) return;
            this.client.setMessages(t.messages);
          }),
        ),
    );
    syncFromCurrentThread(toObservable(this.threadsService.currentThread));
  }

  public send(text: string): void {
    this.sendMessages([{ role: 'user', content: text }]);
  }

  public sendMessages(messages: readonly OutgoingMessage[]): void {
    if (!this.store.currentThreadId()) {
      const id = generateUuid();
      this.startNewThread(id);
    }
    const inputMessages = messages.map(message =>
      message.role === 'user'
        ? this.client.addUserMessage(message.content, message.metadata)
        : toMastraSystemMessage(message.content),
    );
    this.pendingInput.set(inputMessages);
    this.store.markRunStart();
    void this.client.run(generateUuid(), inputMessages);
  }

  public retryLastRun(): void {
    const input = this.pendingInput();
    if (!input) return;
    this.store.markRunStart();
    void this.client.run(generateUuid(), input);
  }

  public cancelRun(): void {
    this.client.cancelRun();
    if (this.store.runStatus() === 'running') this.store.markRunEnd();
  }

  public resetThread(): void {
    this.cancelRun();
    this.store.resetForThread(null);
    this.pendingInput.set(null);
    this.client.setMessages([]);
  }

  public startNewThread(threadId: string): void {
    this.store.resetForThread(threadId);
    this.pendingInput.set(null);
    this.client.setThreadId(threadId);
    this.client.setMessages([]);
    this.client.markThreadAsNew();
  }

  public switchToExistingThread(threadId: string): void {
    this.store.resetForThread(threadId);
    this.pendingInput.set(null);
    this.client.setThreadId(threadId);
    this.client.setMessages([]);
  }

  // History loads emit through the same messages$ stream; while idle we only
  // seed seen ids so a later live run never re-reports replayed tool calls.
  private trackSkillUsage(messages: readonly ChatMessage[]): void {
    const isLiveRun = this.store.runStatus() === 'running';
    for (const message of messages) {
      for (const part of message.parts) {
        if (part.kind !== 'tool-call' || !part.name) continue;
        if (this.seenToolCallIds.has(part.toolCallId)) continue;
        this.seenToolCallIds.add(part.toolCallId);
        if (!isLiveRun) continue;
        if (part.name === 'skill') {
          const skillName = skillNameFromArgs(part.args);
          if (skillName) this.analytics.skillUsed(skillName);
        } else {
          this.analytics.toolUsed(part.name);
        }
      }
    }
  }
}
